import React from "react";
import { ArrowRight, MessageCircle, Globe2, BadgePercent, Star } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-end pt-28 lg:pt-32 pb-14 lg:pb-20 overflow-hidden bg-slate-950"
      data-testid="hero-section"
    >
      {/* Layered background */}
      <div className="absolute inset-0">
        {/* Full-bleed hero photograph — visible on ALL breakpoints incl. mobile */}
        <img
          src="/media/wall-textured-hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-40 md:opacity-45 md:object-left md:w-[62%] md:right-0 md:left-auto md:[mask-image:linear-gradient(90deg,transparent_0%,black_35%,black_100%)]"
          loading="eager"
          decoding="async"
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(180,83,9,0.18),_transparent_55%)]" />
        {/* Mobile: strong bottom-up dark gradient for legibility. Desktop: left-to-right dark. */}
        <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(2,6,23,0.55)_0%,_rgba(2,6,23,0.85)_60%,_#020617_100%)] md:hidden" />
        <div className="hidden md:block absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent md:via-slate-950/60" />
        <div className="absolute inset-0 bg-grain opacity-40" />

        {/* Floating showcase cards — desktop only (real project photos) */}
        <div className="hidden lg:block absolute right-[6%] top-[16%] w-[280px] aspect-[3/4] border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] animate-float-slow overflow-hidden">
          <img src="/media/carpet-broadloom-ballroom.jpg" alt="" aria-hidden="true" className="h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-slate-950 via-slate-950/70 to-transparent">
            <p className="text-[10px] tracking-[0.24em] uppercase text-amber-400">Meridian by the Lawns</p>
            <p className="text-white text-xs mt-0.5">Broadloom ballroom carpet</p>
          </div>
        </div>
        <div className="hidden lg:block absolute right-[24%] top-[46%] w-[200px] aspect-square border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] rotate-[6deg] overflow-hidden">
          <img src="/media/floor-hardwood-herringbone.jpg" alt="" aria-hidden="true" className="h-full w-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent">
            <p className="text-[10px] tracking-[0.22em] uppercase text-amber-400">Chevron parquet</p>
          </div>
        </div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 w-full">
        <div className="max-w-3xl">
          <p className="eyebrow !text-amber-500 mb-5 sm:mb-6 animate-fade-up" data-testid="hero-eyebrow">
            Importers · Distributors · Since 4 decades
          </p>
          <h1
            className="font-display text-white text-[36px] xs:text-[40px] sm:text-6xl lg:text-7xl leading-[1.05] font-medium tracking-tight text-balance animate-fade-up"
            data-testid="hero-title"
          >
            Bangalore&rsquo;s Premier <span className="italic text-amber-500">Importer</span> of Luxury Floorings, Carpets & Wallpapers
          </h1>
          <p
            className="mt-5 sm:mt-7 max-w-2xl text-white/75 text-[15px] sm:text-lg leading-relaxed animate-fade-up"
            style={{ animationDelay: "120ms" }}
            data-testid="hero-subtitle"
          >
            Direct distributor prices on an unmatched variety of high‑quality interior materials. Elevating commercial and residential spaces across Bengaluru since day one — <span className="italic font-display text-amber-400/90">&ldquo;We floor you with style.&rdquo;</span>
          </p>

          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-up" style={{ animationDelay: "220ms" }}>
            <button
              onClick={() => scrollTo("#collection")}
              className="glow-amber group bg-amber-700 hover:bg-amber-800 text-white px-6 sm:px-7 py-4 text-[12px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-3 transition-colors"
              data-testid="hero-cta-explore-collections"
            >
              Explore Our Collections
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a
              href="https://wa.me/919845048406?text=Hi%2C%20I%27m%20interested%20in%20your%20wholesale%20flooring%20%26%20carpet%20range."
              target="_blank"
              rel="noreferrer"
              className="group bg-transparent border border-emerald-400/70 text-emerald-300 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 px-6 sm:px-7 py-4 text-[12px] tracking-[0.24em] uppercase inline-flex items-center justify-center gap-3 transition-colors"
              data-testid="hero-cta-whatsapp"
            >
              <MessageCircle className="h-4 w-4" />
              Chat on WhatsApp
            </a>
          </div>

          {/* Mobile-only marquee of real project thumbnails */}
          <div className="lg:hidden mt-10 -mx-5 px-5 overflow-x-auto no-scrollbar animate-fade-up" style={{ animationDelay: "260ms" }}>
            <div className="flex gap-3 min-w-max pb-1">
              {[
                { src: "/media/carpet-broadloom-ballroom.jpg", label: "Meridian by the Lawns" },
                { src: "/media/floor-hardwood-herringbone.jpg", label: "Chevron parquet lobby" },
                { src: "/media/wall-metallic-geometric.jpg", label: "E-Joy 5 wallpapers" },
                { src: "/media/carpet-auditorium.jpg", label: "Patanjali Auditorium" },
                { src: "/media/turf-sports-arena.jpg", label: "DJ Sports Arena" },
              ].map((p) => (
                <figure
                  key={p.src}
                  className="relative w-[140px] h-[100px] shrink-0 border border-white/10 shadow-lg overflow-hidden"
                  data-testid={`hero-mobile-thumb-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <img src={p.src} alt={p.label} className="h-full w-full object-cover" loading="lazy" />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent p-2">
                    <p className="text-[9px] tracking-[0.18em] uppercase text-amber-400 leading-tight">{p.label}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          {/* Trust badges */}
          <ul className="mt-10 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-5 max-w-3xl animate-fade-up" style={{ animationDelay: "320ms" }}>
            {[
              { icon: <Globe2 className="h-4 w-4" />, label: "Direct Global Importer" },
              { icon: <BadgePercent className="h-4 w-4" />, label: "Best Wholesale Prices" },
              { icon: <Star className="h-4 w-4 fill-amber-500 text-amber-500" />, label: "4.8★ Rated on Google" },
            ].map((b) => (
              <li
                key={b.label}
                className="flex items-center gap-3 border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-sm"
                data-testid={`hero-trust-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <span className="inline-flex h-8 w-8 items-center justify-center bg-amber-700/15 text-amber-400 border border-amber-700/40 shrink-0">
                  {b.icon}
                </span>
                <span className="text-white/85 text-sm tracking-wide">{b.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bottom divider line */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-700/60 to-transparent" />
    </section>
  );
}
